import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Mail, Lock, User, Building, Phone, MapPin, ArrowRight, ArrowLeft, Eye, EyeOff, Upload, FileText, Camera, CheckCircle, Search } from 'lucide-react';
import { verificationApi } from '../services/api';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, label: 'Account', icon: User },
  { id: 2, label: 'Company', icon: Building },
  { id: 3, label: 'Location', icon: MapPin },
  { id: 4, label: 'Documents', icon: FileText },
  { id: 5, label: 'Identity', icon: Camera },
  { id: 6, label: 'Review', icon: CheckCircle },
];

const USER_TYPES = [
  { id: 'buyer', label: 'Buyer' },
  { id: 'supplier', label: 'Supplier' },
  { id: 'trader', label: 'Trader' },
  { id: 'exporter', label: 'Exporter' },
  { id: 'manufacturer', label: 'Manufacturer' },
  { id: 'agent', label: 'Agent / Broker' },
];

const TRADE_LICENSE_ROLES = ['supplier', 'trader', 'exporter', 'manufacturer'];

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(null);

  // Step 1
  const [s1, setS1] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    user_type: searchParams.get('type') || 'buyer',
  });

  // Step 2
  const [s2, setS2] = useState({
    company_name: '', registration_number: '', company_website: '', company_linkedin: '',
    year_established: '', employees: '',
  });

  // Step 3
  const [countries, setCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [companyCountrySearch, setCompanyCountrySearch] = useState('');
  const [tradeSearch, setTradeSearch] = useState('');
  const [s3, setS3] = useState({
    country_of_residence: '', company_country: '', phone_code: '+1', phone_number: '',
    trade_countries: [], main_products: '', annual_trade_volume: '',
  });

  // Step 4
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Step 5 (selfie)
  const [selfieUploaded, setSelfieUploaded] = useState(false);

  // Load countries
  const fetchCountries = useCallback(async (q = '') => {
    try {
      const { data } = await verificationApi.getCountries(q);
      setCountries(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  const filteredCountries = (search) => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  };

  // ========== STEP HANDLERS ==========

  const handleStep1 = async () => {
    setError('');
    if (!s1.name.trim()) { setError('Full name is required.'); return; }
    if (!s1.email.trim()) { setError('Email is required.'); return; }
    if (s1.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(s1.password)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(s1.password)) { setError('Password must contain at least one lowercase letter.'); return; }
    if (!/[0-9]/.test(s1.password)) { setError('Password must contain at least one number.'); return; }
    if (s1.password !== s1.confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      const { data } = await verificationApi.step1(s1);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        setToken(data.access_token);
      }
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    setError('');
    if (!s2.company_name.trim()) { setError('Company name is required.'); return; }
    setLoading(true);
    try {
      await verificationApi.step2(s2);
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save company details.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    setError('');
    if (!s3.country_of_residence) { setError('Please select your country of residence.'); return; }
    if (!s3.company_country) { setError('Please select your company registration country.'); return; }
    if (!s3.phone_number) { setError('Phone number is required.'); return; }
    setLoading(true);
    try {
      await verificationApi.step3(s3);
      setStep(4);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save location details.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDoc = async (file, docType, expiryDate = null) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      if (expiryDate) formData.append('expiry_date', expiryDate);
      const { data } = await verificationApi.uploadDoc(formData);
      setDocuments(prev => [...prev, data.document]);
      toast.success(`${docType.replace('_', ' ')} uploaded successfully.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleSelfieUpload = async (file) => {
    await handleUploadDoc(file, 'selfie');
    setSelfieUploaded(true);
    try { await verificationApi.step5(); } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await verificationApi.submit();
      toast.success('Application submitted for review!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const needsTradeLicense = TRADE_LICENSE_ROLES.includes(s1.user_type);
  const docTypes = documents.map(d => d.doc_type);

  // Country selector helper component
  const CountrySelect = ({ value, onChange, search, setSearch, placeholder, testId }) => (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search || countries.find(c => c.code === value)?.name || ''}
          onChange={(e) => { setSearch(e.target.value); onChange(''); }}
          onFocus={() => setSearch(search || '')}
          placeholder={placeholder}
          className="pl-9"
          data-testid={testId}
        />
      </div>
      {search !== null && search !== undefined && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredCountries(search).slice(0, 30).map(c => (
            <button
              key={c.code}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              onClick={() => { onChange(c.code); setSearch(null); }}
            >
              <span className="text-slate-500 text-xs w-8">{c.code}</span>
              <span>{c.name}</span>
              <span className="ml-auto text-slate-400 text-xs">{c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:block lg:w-[380px] bg-[#0A192F] relative overflow-hidden">
        <div className="p-8 h-full flex flex-col">
          <Link to="/" className="flex items-center mb-12">
            <span className="font-extrabold text-[20px] text-white tracking-tight">eaglesmarket.com</span>
          </Link>

          <div className="flex-1">
            <h2 className="text-white text-xl font-bold mb-2">Business Verification</h2>
            <p className="text-slate-400 text-sm mb-8">Complete all steps to get verified and unlock full trading access.</p>

            {/* Step progress */}
            <div className="space-y-1">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive ? 'bg-white/10' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      isDone ? 'bg-green-500 text-white' : isActive ? 'bg-[#EA580C] text-white' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {isDone ? <CheckCircle className="w-4 h-4" /> : s.id}
                    </div>
                    <span className={`text-sm ${isActive ? 'text-white font-semibold' : isDone ? 'text-green-400' : 'text-slate-500'}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-slate-500 text-xs">
            Already have an account?{' '}
            <Link to="/login" className="text-[#EA580C] hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-start justify-center p-6 sm:p-8 overflow-y-auto bg-[#F8F9FB]">
        <div className="w-full max-w-lg py-4">
          {/* Mobile step indicator */}
          <div className="lg:hidden flex items-center gap-1 mb-6">
            {STEPS.map(s => (
              <div key={s.id} className={`h-1.5 flex-1 rounded-full ${step >= s.id ? 'bg-[#EA580C]' : 'bg-slate-200'}`} />
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          {/* ========== STEP 1: Account ========== */}
          {step === 1 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Create your account</h1>
              <p className="text-slate-500 text-sm mb-6">Step 1 of 6 — Account details</p>

              <div className="mb-5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">I am a</Label>
                <div className="grid grid-cols-3 gap-2">
                  {USER_TYPES.map(t => (
                    <button
                      key={t.id} type="button"
                      onClick={() => setS1({ ...s1, user_type: t.id })}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        s1.user_type === t.id ? 'border-[#0A192F] bg-[#0A192F]/5 text-[#0A192F]' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                      data-testid={`type-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="name" value={s1.name} onChange={e => setS1({ ...s1, name: e.target.value })} placeholder="John Doe" className="pl-9" data-testid="reg-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Business Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="email" type="email" value={s1.email} onChange={e => setS1({ ...s1, email: e.target.value })} placeholder="you@company.com" className="pl-9" data-testid="reg-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={s1.password} onChange={e => setS1({ ...s1, password: e.target.value })} placeholder="Min 8 chars, upper + lower + number" className="pl-9 pr-10" data-testid="reg-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="confirm" type="password" value={s1.confirmPassword} onChange={e => setS1({ ...s1, confirmPassword: e.target.value })} placeholder="Re-enter password" className="pl-9" data-testid="reg-confirm" />
                  </div>
                </div>
              </div>

              <Button className="w-full mt-6 bg-[#0A192F] hover:bg-[#172A46]" onClick={handleStep1} disabled={loading} data-testid="step1-next">
                {loading ? 'Creating account...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}

          {/* ========== STEP 2: Company ========== */}
          {step === 2 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Company Details</h1>
              <p className="text-slate-500 text-sm mb-6">Step 2 of 6 — Tell us about your business</p>
              <div className="space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input value={s2.company_name} onChange={e => setS2({ ...s2, company_name: e.target.value })} placeholder="Acme Trading LLC" className="mt-1" data-testid="reg-company" />
                </div>
                <div>
                  <Label>Registration / License Number</Label>
                  <Input value={s2.registration_number} onChange={e => setS2({ ...s2, registration_number: e.target.value })} placeholder="e.g., CR-12345" className="mt-1" data-testid="reg-regnum" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Year Established</Label>
                    <Input type="number" value={s2.year_established} onChange={e => setS2({ ...s2, year_established: e.target.value })} placeholder="2015" className="mt-1" />
                  </div>
                  <div>
                    <Label>Employees</Label>
                    <select value={s2.employees} onChange={e => setS2({ ...s2, employees: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                      <option value="">Select</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Company Website</Label>
                  <Input value={s2.company_website} onChange={e => setS2({ ...s2, company_website: e.target.value })} placeholder="https://www.yourcompany.com" className="mt-1" />
                </div>
                <div>
                  <Label>Company LinkedIn</Label>
                  <Input value={s2.company_linkedin} onChange={e => setS2({ ...s2, company_linkedin: e.target.value })} placeholder="https://linkedin.com/company/..." className="mt-1" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="flex-1 bg-[#0A192F] hover:bg-[#172A46]" onClick={handleStep2} disabled={loading} data-testid="step2-next">
                  {loading ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 3: Country & Trade ========== */}
          {step === 3 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Location & Trade Activity</h1>
              <p className="text-slate-500 text-sm mb-6">Step 3 of 6 — Where you operate</p>
              <div className="space-y-4">
                <div>
                  <Label>Country of Residence *</Label>
                  <CountrySelect
                    value={s3.country_of_residence}
                    onChange={(v) => setS3({ ...s3, country_of_residence: v })}
                    search={countrySearch}
                    setSearch={setCountrySearch}
                    placeholder="Search country..."
                    testId="reg-country"
                  />
                </div>
                <div>
                  <Label>Company Registration Country *</Label>
                  <CountrySelect
                    value={s3.company_country}
                    onChange={(v) => setS3({ ...s3, company_country: v })}
                    search={companyCountrySearch}
                    setSearch={setCompanyCountrySearch}
                    placeholder="Search country..."
                    testId="reg-company-country"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={s3.phone_code}
                      onChange={e => setS3({ ...s3, phone_code: e.target.value })}
                      className="w-28 h-10 rounded-md border border-slate-200 px-2 text-sm"
                      data-testid="reg-phone-code"
                    >
                      {countries.map(c => (
                        <option key={c.code} value={c.phone}>{c.phone} {c.code}</option>
                      ))}
                    </select>
                    <Input
                      value={s3.phone_number}
                      onChange={e => setS3({ ...s3, phone_number: e.target.value })}
                      placeholder="Phone number"
                      className="flex-1"
                      data-testid="reg-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label>Countries You Trade With</Label>
                  <div className="relative mt-1">
                    <Input
                      value={tradeSearch || ''}
                      onChange={e => setTradeSearch(e.target.value)}
                      placeholder="Search and select countries..."
                    />
                    {tradeSearch && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-36 overflow-y-auto">
                        {filteredCountries(tradeSearch).slice(0, 20).map(c => (
                          <button
                            key={c.code} type="button"
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                            onClick={() => {
                              if (!s3.trade_countries.includes(c.code)) {
                                setS3({ ...s3, trade_countries: [...s3.trade_countries, c.code] });
                              }
                              setTradeSearch('');
                            }}
                          >
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {s3.trade_countries.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {s3.trade_countries.map(code => {
                        const c = countries.find(x => x.code === code);
                        return (
                          <span key={code} className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            {c?.name || code}
                            <button type="button" onClick={() => setS3({ ...s3, trade_countries: s3.trade_countries.filter(x => x !== code) })} className="text-slate-400 hover:text-red-500">x</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Main Products / Services</Label>
                  <Input value={s3.main_products} onChange={e => setS3({ ...s3, main_products: e.target.value })} placeholder="e.g., Industrial Electronics, Machinery" className="mt-1" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="flex-1 bg-[#0A192F] hover:bg-[#172A46]" onClick={handleStep3} disabled={loading} data-testid="step3-next">
                  {loading ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 4: Documents ========== */}
          {step === 4 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Document Upload</h1>
              <p className="text-slate-500 text-sm mb-6">Step 4 of 6 — Upload verification documents</p>

              <div className="space-y-4">
                {[
                  { type: 'trade_license', label: 'Trade License / Certificate of Incorporation', required: needsTradeLicense },
                  { type: 'tax_certificate', label: 'Tax / VAT / TIN Certificate', required: false },
                  { type: 'national_id', label: 'Passport or National ID', required: true },
                  { type: 'proof_of_address', label: 'Proof of Address', required: false },
                  { type: 'bank_reference', label: 'Bank Reference (Optional — Premium Trust)', required: false },
                ].map(doc => {
                  const uploaded = docTypes.includes(doc.type);
                  return (
                    <div key={doc.type} className={`border rounded-lg p-4 ${uploaded ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {uploaded ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Upload className="w-4 h-4 text-slate-400" />}
                          <span className="text-sm font-medium text-[#0A192F]">{doc.label}</span>
                        </div>
                        {doc.required && <span className="text-[10px] text-red-500 font-bold uppercase">Required</span>}
                      </div>
                      {uploaded ? (
                        <p className="text-xs text-green-600 ml-6">Uploaded</p>
                      ) : (
                        <label className="ml-6 inline-flex items-center gap-1.5 text-xs text-[#EA580C] cursor-pointer hover:underline">
                          <Upload className="w-3.5 h-3.5" />
                          Choose file (JPG, PNG, PDF, max 10MB)
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            className="hidden"
                            onChange={e => { if (e.target.files[0]) handleUploadDoc(e.target.files[0], doc.type); }}
                            disabled={uploading}
                            data-testid={`upload-${doc.type}`}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              {uploading && <p className="text-sm text-slate-500 mt-3 text-center">Uploading...</p>}

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="flex-1 bg-[#0A192F] hover:bg-[#172A46]" onClick={() => setStep(5)} data-testid="step4-next">
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 5: Identity ========== */}
          {step === 5 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Identity Verification</h1>
              <p className="text-slate-500 text-sm mb-6">Step 5 of 6 — Upload a photo for identity check</p>

              <div className={`border rounded-lg p-6 text-center ${selfieUploaded ? 'border-green-200 bg-green-50/30' : 'border-dashed border-slate-300'}`}>
                {selfieUploaded ? (
                  <div>
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="font-semibold text-green-700">Photo uploaded</p>
                    <p className="text-xs text-slate-500 mt-1">Our team will review your identity documents.</p>
                  </div>
                ) : (
                  <div>
                    <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600 mb-1">Upload a clear photo of yourself</p>
                    <p className="text-xs text-slate-400 mb-4">Holding your ID next to your face (optional but recommended)</p>
                    <label className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#172A46]">
                      <Camera className="w-4 h-4" /> Upload Photo
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={e => { if (e.target.files[0]) handleSelfieUpload(e.target.files[0]); }}
                        disabled={uploading}
                        data-testid="upload-selfie"
                      />
                    </label>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-3 text-center">
                This step is optional for buyers. Required for supplier verification.
              </p>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(4)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="flex-1 bg-[#0A192F] hover:bg-[#172A46]" onClick={() => setStep(6)} data-testid="step5-next">
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========== STEP 6: Review & Submit ========== */}
          {step === 6 && (
            <div>
              <h1 className="text-2xl font-bold text-[#0A192F] mb-1">Review & Submit</h1>
              <p className="text-slate-500 text-sm mb-6">Step 6 of 6 — Review your application</p>

              <div className="space-y-4">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Account</h3>
                  <p className="text-sm"><span className="text-slate-500">Name:</span> <span className="font-medium">{s1.name}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Email:</span> <span className="font-medium">{s1.email}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Type:</span> <span className="font-medium capitalize">{s1.user_type}</span></p>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Company</h3>
                  <p className="text-sm"><span className="text-slate-500">Company:</span> <span className="font-medium">{s2.company_name}</span></p>
                  {s2.registration_number && <p className="text-sm"><span className="text-slate-500">Reg #:</span> <span className="font-medium">{s2.registration_number}</span></p>}
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Location</h3>
                  <p className="text-sm"><span className="text-slate-500">Residence:</span> <span className="font-medium">{countries.find(c => c.code === s3.country_of_residence)?.name || s3.country_of_residence}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Company Country:</span> <span className="font-medium">{countries.find(c => c.code === s3.company_country)?.name || s3.company_country}</span></p>
                  <p className="text-sm"><span className="text-slate-500">Phone:</span> <span className="font-medium">{s3.phone_code} {s3.phone_number}</span></p>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Documents ({documents.length})</h3>
                  {documents.length === 0 ? (
                    <p className="text-sm text-slate-400">No documents uploaded</p>
                  ) : (
                    <div className="space-y-1">
                      {documents.map(d => (
                        <div key={d.doc_id} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-slate-600 capitalize">{d.doc_type.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(5)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="flex-1 bg-[#EA580C] hover:bg-[#C2410C]" onClick={handleSubmit} disabled={loading} data-testid="submit-application">
                  {loading ? 'Submitting...' : 'Submit Application'} <CheckCircle className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
